import React from 'react';
import { MessageCircle, Award, Building2, ShieldCheck } from 'lucide-react';
import { whyFieldstechData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  MessageCircle: MessageCircle,
  Award: Award,
  Building2: Building2,
  ShieldCheck: ShieldCheck
};

const WhyFieldstech = () => {
  const { ref: whyRef, isVisible } = useScrollAnimation();

  return (
    <section id="por-que" className="relative bg-gradient-to-br from-[#0A1E3D] via-[#0C2340] to-[#0F172A] py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute top-1/4 -left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 -right-1/4 w-[600px] h-[600px] bg-gradient-to-l from-orange-500/20 to-amber-500/20 rounded-full blur-3xl animate-pulse-slower"></div>
      </div>

      {/* Decorative grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none"></div>

      <div ref={whyRef} className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 scroll-animate ${isVisible ? 'in-view' : ''}`}>
            {whyFieldstechData.sectionTitle}
          </h2>
          <p className={`text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed scroll-animate delay-200 ${isVisible ? 'in-view' : ''}`}>
            {whyFieldstechData.sectionSubtitle}
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
          {whyFieldstechData.reasons.map((reason, index) => {
            const IconComponent = iconMap[reason.icon];
            const delay = `delay-${(index + 1) * 200}`;
            return (
              <div
                key={reason.id}
                className={`group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-[1.02] scroll-animate ${delay} ${isVisible ? 'in-view' : ''}`}
              >
                {/* Gradient glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-orange-500/0 group-hover:from-emerald-500/10 group-hover:to-orange-500/10 rounded-2xl transition-all duration-300"></div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-5 sm:mb-6">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:shadow-emerald-500/50 group-hover:scale-110 transition-all duration-300">
                      <IconComponent className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                    {reason.title}
                  </h3>

                  {/* Description */}
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom accent line */}
        <div className="mt-12 sm:mt-16 lg:mt-20 flex justify-center">
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-500 to-orange-500 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default WhyFieldstech;
